import { getSession, sessionKey, subscribeSession } from './auth';
import { API_BASE } from './config';
import type { UniverseSort } from './types';

export { resolveSort } from './sort';

/**
 * A visitor's deliberate sort choice. Only an explicit pick from the sort menu
 * is written — never a `?sort=` arriving on a shared link, and never the
 * ordering older builds applied implicitly (those used no storage key, so
 * nothing legacy can be mistaken for a preference).
 *
 * Device storage always holds it; signed in, the account copy on the server
 * wins, and a device choice made before signing in is uploaded when the
 * account has none yet.
 */
const KEY = 'universe.sort.v1';
const ALL: readonly UniverseSort[] = ['relevant', 'newest', 'updated', 'az', 'za'];
const PREFS_URL = `${API_BASE}/api/universe/v1/me/preferences`;

type Listener = () => void;
const listeners = new Set<Listener>();
let current: UniverseSort | null = read();

function isSort(value: unknown): value is UniverseSort {
  return ALL.includes(value as UniverseSort);
}

function read(): UniverseSort | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const value = (JSON.parse(raw) as { value?: unknown }).value;
    return isSort(value) ? value : null;
  } catch {
    return null;
  }
}

function setLocal(value: UniverseSort) {
  current = value;
  try {
    localStorage.setItem(KEY, JSON.stringify({ value, chosenAt: new Date().toISOString() }));
  } catch {
    /* storage unavailable: the choice lasts for this page */
  }
  listeners.forEach((l) => l());
}

export function getSortPreference(): UniverseSort | null {
  return current;
}

export function subscribeSortPreference(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function remote(method: 'GET' | 'PUT', body?: { sort: UniverseSort }): Promise<UniverseSort | null> {
  const res = await fetch(PREFS_URL, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(method === 'PUT' ? { 'Content-Type': 'application/json', 'X-Requested-With': 'universe' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`preferences ${res.status}`);
  const payload = (await res.json()) as { data?: { sort?: unknown } };
  return isSort(payload.data?.sort) ? payload.data.sort : null;
}

export function saveSortPreference(value: UniverseSort) {
  setLocal(value);
  if (getSession()) remote('PUT', { sort: value }).catch(() => undefined);
}

let syncedFor = '';

/** Pulls the account preference (or seeds it from this device) once per signed-in identity. */
export async function syncSortPreference(): Promise<void> {
  const key = sessionKey(getSession());
  if (!key) {
    syncedFor = '';
    return;
  }
  if (key === syncedFor) return;
  syncedFor = key;
  try {
    const saved = await remote('GET');
    if (saved) {
      if (saved !== current) setLocal(saved);
    } else if (current) {
      await remote('PUT', { sort: current });
    }
  } catch {
    // Retried on the next identity change; the device choice keeps working meanwhile.
    syncedFor = '';
  }
}

if (typeof window !== 'undefined') {
  subscribeSession(() => void syncSortPreference());
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY && e.key !== null) return;
    current = read();
    listeners.forEach((l) => l());
  });
}
